import axios from 'axios';
import { SERVICES_API_BASE_URL, DIAGNOSTIC_API_BASE_URL } from './apiConfig';

const api = axios.create({
  baseURL: SERVICES_API_BASE_URL,
});

const diagnosticsApi = axios.create({
  baseURL: DIAGNOSTIC_API_BASE_URL,
});

diagnosticsApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.data?.code === 'CAPTCHA_FAILED') {
      error.message = error.response.data.message || 'Please complete the CAPTCHA verification.';
    }
    return Promise.reject(error);
  }
);

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

diagnosticsApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export enum DiagnosticType {
  Website = 1,
  Marketing = 2,
  Business = 3,
  Talent = 4,
  Recruitment = 5,
}

export interface TalentDiagnosticsDefinition {
  id?: string;
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface TalentDiagnosticsDefinitionDetail extends TalentDiagnosticsDefinition {
  sections?: TalentDiagnosticsSection[];
  questions?: TalentDiagnosticsQuestion[];
  benchmarks?: TalentDiagnosticsBenchmark[];
  recommendations?: TalentDiagnosticsRecommendation[];
}

export interface TalentDiagnosticsSection {
  id?: string;
  diagnosticDefinitionId?: string;
  name?: string;
  description?: string;
  weighting?: number;
  isActive?: boolean;
  isActiveFlag?: boolean;
  sortOrder?: number;
}

export interface TalentDiagnosticsQuestion {
  id?: string;
  sectionId?: string;
  key?: string;
  text?: string;
  description?: string;
  type?: string;
  isRequired?: boolean;
  weighting?: number;

  scoringType?: string;
  scoringConfigJson?: string;

  isActive?: boolean;
  isActiveFlag?: boolean;

  options?: Array<{ key?: string; text?: string; score?: number }>;
  optionsJson?: string | null;
  sortOrder?: number;
}
export interface TalentDiagnosticsBenchmark {
  id?: string;
  diagnosticDefinitionId?: string;
  name?: string;
  minScore?: number;
  maxScore?: number;
  recommendationJson?: string;
  isActive?: boolean;
  isActiveFlag?: boolean;
}

export interface TalentDiagnosticsRecommendation {
  id?: string;
  diagnosticDefinitionId?: string;
  title?: string;
  bodyJson?: string;
  minScore?: number;
  maxScore?: number;
  isActive?: boolean;
  isActiveFlag?: boolean;
}

export const getTalentDiagnosticsDefinitions = async (): Promise<TalentDiagnosticsDefinition[]> => {
  const response = await api.get<TalentDiagnosticsDefinition[]>(`/api/talentadmin/definitions`);
  return response.data || [];
};

export const getPublicTalentDiagnosticsDefinitions = async (): Promise<TalentDiagnosticsDefinition[]> => {
  const response = await diagnosticsApi.get<TalentDiagnosticsDefinition[]>(`/api/TalentDiagnostics/definitions`);
  return response.data || [];
};

export const getPublicTalentDiagnosticsDefinitionById = async (id: string): Promise<TalentDiagnosticsDefinitionDetail> => {
  const response = await diagnosticsApi.get<TalentDiagnosticsDefinitionDetail>(`/api/TalentDiagnostics/definitions/${id}`);
  return response.data;
};

export interface TalentDiagnosticRequest {
  diagnosticDefinitionId?: string;
  name?: string;
  company?: string;
  email?: string;
  answers: Record<string, string>;
  captchaToken: string;
  diagnosticType: DiagnosticType;
}

export interface TalentDiagnosticPreflightRequest {
  diagnosticDefinitionId?: string;
  answers: Record<string, string>;
  captchaToken?: never;
}

export const analyzeTalentDiagnostics = async (payload: TalentDiagnosticRequest | TalentDiagnosticPreflightRequest): Promise<any> => {
  const response = await diagnosticsApi.post(`/api/TalentDiagnostics/analyze`, payload);
  return response.data;
};

export const getTalentDiagnosticsSections = async (definitionId: string): Promise<TalentDiagnosticsSection[]> => {
  const response = await api.get<TalentDiagnosticsSection[]>(`/api/talentadmin/sections`, {
    params: { definitionId },
  });
  return response.data || [];
};

export const getTalentDiagnosticsSectionById = async (id: string): Promise<TalentDiagnosticsSection> => {
  const response = await api.get<TalentDiagnosticsSection>(`/api/talentadmin/sections/${id}`);
  return response.data;
};

export const createTalentDiagnosticsSection = async (payload: Partial<TalentDiagnosticsSection>): Promise<TalentDiagnosticsSection> => {
  const response = await api.post<TalentDiagnosticsSection>(`/api/talentadmin/sections`, payload);
  return response.data;
};

export const updateTalentDiagnosticsSection = async (id: string, payload: Partial<TalentDiagnosticsSection>): Promise<TalentDiagnosticsSection> => {
  const response = await api.put<TalentDiagnosticsSection>(`/api/talentadmin/sections/${id}`, payload);
  return response.data;
};

export const deleteTalentDiagnosticsSection = async (id: string): Promise<void> => {
  await api.delete(`/api/talentadmin/sections/${id}`);
};

export const reorderTalentDiagnosticsSections = async (payload: { diagnosticDefinitionId: string; orderedSectionIds: string[] }): Promise<void> => {
  await api.post(`/api/talentadmin/sections/reorder`, payload);
};

export const getTalentDiagnosticsQuestions = async (sectionId: string): Promise<TalentDiagnosticsQuestion[]> => {
  const response = await api.get<TalentDiagnosticsQuestion[]>(`/api/talentadmin/sections/${sectionId}/questions`);
  return response.data || [];
};

export const getTalentDiagnosticsQuestionById = async (id: string): Promise<TalentDiagnosticsQuestion> => {
  const response = await api.get<TalentDiagnosticsQuestion>(`/api/talentadmin/questions/${id}`);
  return response.data;
};

export const createTalentDiagnosticsQuestion = async (payload: Partial<TalentDiagnosticsQuestion>): Promise<TalentDiagnosticsQuestion> => {
  const response = await api.post<TalentDiagnosticsQuestion>(`/api/talentadmin/questions`, payload);
  return response.data;
};

export const updateTalentDiagnosticsQuestion = async (id: string, payload: Partial<TalentDiagnosticsQuestion>): Promise<TalentDiagnosticsQuestion> => {
  const response = await api.put<TalentDiagnosticsQuestion>(`/api/talentadmin/questions/${id}`, payload);
  return response.data;
};

export const deleteTalentDiagnosticsQuestion = async (id: string): Promise<void> => {
  await api.delete(`/api/talentadmin/questions/${id}`);
};

export const reorderTalentDiagnosticsQuestions = async (payload: { sectionId: string; orderedQuestionIds: string[] }): Promise<void> => {
  await api.post(`/api/talentadmin/questions/reorder`, payload);
};

export const addTalentDiagnosticsQuestionOption = async (questionId: string, payload: Array<{ key: string; text: string; score?: number }>): Promise<void> => {
  await api.post(`/api/talentadmin/questions/${questionId}/options`, payload);
};

export const getTalentDiagnosticsBenchmarks = async (definitionId: string): Promise<TalentDiagnosticsBenchmark[]> => {
  const response = await api.get<TalentDiagnosticsBenchmark[]>(`/api/talentadmin/benchmarks`, {
    params: { definitionId },
  });
  return response.data || [];
};

export const getTalentDiagnosticsBenchmarkById = async (id: string): Promise<TalentDiagnosticsBenchmark> => {
  const response = await api.get<TalentDiagnosticsBenchmark>(`/api/talentadmin/benchmarks/${id}`);
  return response.data;
};

export const createTalentDiagnosticsBenchmark = async (payload: Partial<TalentDiagnosticsBenchmark>): Promise<TalentDiagnosticsBenchmark> => {
  const response = await api.post<TalentDiagnosticsBenchmark>(`/api/talentadmin/benchmarks`, payload);
  return response.data;
};

export const updateTalentDiagnosticsBenchmark = async (id: string, payload: Partial<TalentDiagnosticsBenchmark>): Promise<TalentDiagnosticsBenchmark> => {
  const response = await api.put<TalentDiagnosticsBenchmark>(`/api/talentadmin/benchmarks/${id}`, payload);
  return response.data;
};

export const deleteTalentDiagnosticsBenchmark = async (id: string): Promise<void> => {
  await api.delete(`/api/talentadmin/benchmarks/${id}`);
};

export const getTalentDiagnosticsRecommendations = async (definitionId: string): Promise<TalentDiagnosticsRecommendation[]> => {
  const response = await api.get<TalentDiagnosticsRecommendation[]>(`/api/talentadmin/recommendations`, {
    params: { definitionId },
  });
  return response.data || [];
};

export const getTalentDiagnosticsRecommendationById = async (id: string): Promise<TalentDiagnosticsRecommendation> => {
  const response = await api.get<TalentDiagnosticsRecommendation>(`/api/talentadmin/recommendations/${id}`);
  return response.data;
};

export const createTalentDiagnosticsRecommendation = async (payload: Partial<TalentDiagnosticsRecommendation>): Promise<TalentDiagnosticsRecommendation> => {
  const response = await api.post<TalentDiagnosticsRecommendation>(`/api/talentadmin/recommendations`, payload);
  return response.data;
};

export const updateTalentDiagnosticsRecommendation = async (id: string, payload: Partial<TalentDiagnosticsRecommendation>): Promise<TalentDiagnosticsRecommendation> => {
  const response = await api.put<TalentDiagnosticsRecommendation>(`/api/talentadmin/recommendations/${id}`, payload);
  return response.data;
};

export const deleteTalentDiagnosticsRecommendation = async (id: string): Promise<void> => {
  await api.delete(`/api/talentadmin/recommendations/${id}`);
};
